/**
 * Members List Page
 * Display and manage all members
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Download } from 'lucide-react';

import { fetchMembers } from '../memberSlice';
import Table from '../../../components/common/Table/Table';
import Button from '../../../components/common/Button/Button';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import { formatCurrency } from '../../../utils/formatCurrency';
import { formatDate } from '../../../utils/formatDate';
import { ROUTES, routeHelpers } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import PERMISSIONS from '../../../constants/permissions';

/**
 * Members List Page Component
 * @returns {JSX.Element} Members list page
 */
const MembersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { members, isLoading, pagination } = useSelector(
    (state) => state.members
  );
  const { hasPermission } = usePermissions();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    dispatch(fetchMembers({ page: 1, limit: 10 }));
  }, [dispatch]);

  const columns = [
    {
      header: 'Member ID',
      accessorKey: 'memberId',
      cell: ({ row }) => (
        <span className="font-medium text-blue-600">{row.original.memberId}</span>
      ),
    },
    {
      header: 'Name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      header: 'Phone',
      accessorKey: 'phone',
    },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => {
        const status = row.original.status;
        const statusClasses = {
          active: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
          inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
          suspended: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
          pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
        };
        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              statusClasses[status] || statusClasses.inactive
            }`}
          >
            {status}
          </span>
        );
      },
    },
    {
      header: 'Joined Date',
      accessorKey: 'createdAt',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      header: 'Savings Balance',
      accessorKey: 'savingsBalance',
      cell: ({ row }) => formatCurrency(row.original.savingsBalance || 0),
    },
  ];

  const handleRowClick = (row) => {
    navigate(routeHelpers.memberProfile(row.id));
  };

  const canCreateMember = hasPermission(PERMISSIONS.MEMBERS_CREATE);
  const canExport = hasPermission(PERMISSIONS.MEMBERS_EXPORT);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Members
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage and view all SACCOS members
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <Button variant="secondary">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
          {canCreateMember && (
            <Button
              variant="primary"
              onClick={() => navigate(ROUTES.MEMBERS.CREATE)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Member
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="pending">Pending</option>
          </select>
        </div>
      </div>

      {/* Members Table */}
      {members.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <Table
            data={members}
            columns={columns}
            isLoading={isLoading}
            onRowClick={handleRowClick}
          />
        </div>
      ) : (
        <EmptyState
          title="No members found"
          description="Get started by adding your first member to the SACCOS."
          actionLabel={canCreateMember ? 'Add Member' : null}
          onAction={
            canCreateMember ? () => navigate(ROUTES.MEMBERS.CREATE) : null
          }
        />
      )}
    </div>
  );
};

export default MembersList;
